"use client";

import React from "react";
import { motion } from "framer-motion";

export interface TalktribeHowItWorksBlockProps {
    tag?: string;
    title?: string;
    steps?: Array<{
        step: string;
        title: string;
        desc: string;
    }>;
}

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 },
};

export default function TalktribeHowItWorksBlock({
    tag = "User Flow",
    title = "How TalkTribe Works",
    steps = [
        { step: "01", title: "Discover or Create", desc: "Browse the character gallery by category, or describe a new character in your own words to create a custom AI persona instantly." },
        { step: "02", title: "Start Chatting", desc: "Select a character and begin a conversation. The AI responds in-character with unique personality, voice, and knowledge consistent with its persona." },
        { step: "03", title: "Build Relationships", desc: "Characters remember your conversations. Come back anytime and pick up where you left off — the AI recalls context and builds on previous interactions." }
    ]
}: TalktribeHowItWorksBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#060608] w-full" data-header-theme="light">
            <div className="container mx-auto">
                <div className="max-w-2xl mb-16">
                    {tag && (
                        <span className="text-xs font-mono text-violet-400 uppercase tracking-widest block mb-4">// {tag}</span>
                    )}
                    <h2 className="text-3xl md:text-5xl font-light text-white leading-tight">{title}</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {steps.map((item, i) => (
                        <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                            className="relative bg-[#0d0d10] border border-zinc-900 rounded-xl p-8 space-y-4 hover:border-zinc-800 transition-colors w-full">
                            <span className="text-5xl font-mono font-bold text-zinc-900 block">{item.step}</span>
                            <h3 className="text-xl font-medium text-white">{item.title}</h3>
                            <p className="text-zinc-500 text-sm leading-relaxed font-light">{item.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
